// Cloud media import — Google Drive (Picker API), Google Photos (Picker API)
// and Dropbox (Chooser). All flows are 100% client-side: the guest authorizes
// against THEIR OWN account, the browser downloads the bytes, and the files
// join the normal upload pipeline as File objects. No cloud credentials ever
// touch our server.
//
// Requires (frontend/.env — safe browser identifiers, not secrets):
//   REACT_APP_GOOGLE_CLIENT_ID            OAuth Web client ID
//   REACT_APP_GOOGLE_BROWSER_KEY          API key (restricted to Picker/Drive)
//   REACT_APP_GOOGLE_CLOUD_PROJECT_NUMBER Cloud project number (Picker appId)
//   REACT_APP_DROPBOX_APP_KEY             Dropbox Chooser app key

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GOOGLE_BROWSER_KEY = process.env.REACT_APP_GOOGLE_BROWSER_KEY;
const GOOGLE_PROJECT_NUMBER = process.env.REACT_APP_GOOGLE_CLOUD_PROJECT_NUMBER;
const DROPBOX_APP_KEY = process.env.REACT_APP_DROPBOX_APP_KEY;

const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";
const PHOTOS_SCOPE = "https://www.googleapis.com/auth/photospicker.mediaitems.readonly";
const GOOGLE_SCOPES = `${DRIVE_SCOPE} ${PHOTOS_SCOPE}`;

export const cloudConfig = {
  googleDrive: Boolean(GOOGLE_CLIENT_ID && GOOGLE_BROWSER_KEY && GOOGLE_PROJECT_NUMBER),
  googlePhotos: Boolean(GOOGLE_CLIENT_ID),
  dropbox: Boolean(DROPBOX_APP_KEY),
};

let googleInited = false;
let tokenClient = null;
let googleAccessToken = null;

function loadScript(src, id, attrs = {}) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const s = document.createElement("script");
    s.id = id;
    s.src = src;
    s.async = true;
    Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
    s.onload = resolve;
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

async function initGoogle() {
  if (googleInited) return;
  await Promise.all([
    loadScript("https://apis.google.com/js/api.js", "google-api-js"),
    loadScript("https://accounts.google.com/gsi/client", "google-gis-js"),
  ]);
  await new Promise((resolve) => window.gapi.load("picker", resolve));
  tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: GOOGLE_CLIENT_ID,
    scope: GOOGLE_SCOPES,
    callback: () => {},
  });
  googleInited = true;
}

function requestGoogleToken(prompt) {
  return new Promise((resolve, reject) => {
    tokenClient.callback = (response) => {
      if (response.error) return reject(new Error(response.error_description || response.error));
      googleAccessToken = response.access_token;
      resolve(googleAccessToken);
    };
    tokenClient.requestAccessToken({ prompt });
  });
}

async function getGoogleToken() {
  await initGoogle();
  return requestGoogleToken(googleAccessToken ? "" : "consent");
}

function safeName(name, fallback) {
  return (name || fallback).replace(/[\\/:*?"<>|]/g, "_");
}

async function blobAsFile(response, name, type) {
  if (!response.ok) throw new Error(`Download failed (${response.status})`);
  const blob = await response.blob();
  return new File([blob], safeName(name, "imported-media"), {
    type: type || blob.type || "application/octet-stream",
  });
}

// ---------- Google Drive ----------
export async function pickDriveFiles({ onDownloadStart } = {}) {
  const token = await getGoogleToken();
  return new Promise((resolve, reject) => {
    const view = new window.google.picker.View(window.google.picker.ViewId.DOCS);
    view.setMimeTypes("image/png,image/jpeg,image/gif,image/webp,image/heic,video/mp4,video/quicktime,video/webm");
    const picker = new window.google.picker.PickerBuilder()
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .setDeveloperKey(GOOGLE_BROWSER_KEY)
      .setAppId(GOOGLE_PROJECT_NUMBER)
      .setOAuthToken(token)
      .addView(view)
      .setCallback(async (data) => {
        if (data.action === window.google.picker.Action.CANCEL) return resolve([]);
        if (data.action !== window.google.picker.Action.PICKED) return;
        try {
          const docs = data[window.google.picker.Response.DOCUMENTS] || [];
          onDownloadStart?.(docs.length);
          const files = await Promise.all(
            docs.map(async (doc) => {
              const id = doc[window.google.picker.Document.ID];
              const metaRes = await fetch(
                `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(id)}?fields=id,name,mimeType,size`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (!metaRes.ok) throw new Error(`Drive metadata failed (${metaRes.status})`);
              const meta = await metaRes.json();
              const content = await fetch(
                `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(id)}?alt=media`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              return blobAsFile(content, meta.name, meta.mimeType);
            })
          );
          resolve(files);
        } catch (e) {
          reject(e);
        }
      })
      .build();
    picker.setVisible(true);
  });
}

// ---------- Google Photos (Picker API — session flow) ----------
async function photosApi(path, token, options = {}) {
  const response = await fetch(`https://photospicker.googleapis.com/v1${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
  if (!response.ok) throw new Error(`Google Photos error (${response.status})`);
  return response.status === 204 ? null : response.json();
}

export async function pickGooglePhotos({ onDownloadStart } = {}) {
  const token = await getGoogleToken();
  const session = await photosApi("/sessions", token, {
    method: "POST",
    body: JSON.stringify({}),
  });

  // Must open synchronously-ish from the user gesture or popup blockers bite.
  const tab = window.open(session.pickerUri, "google-photos-picker");
  if (!tab) throw new Error("Please allow pop-ups to pick from Google Photos");

  try {
    let state;
    const deadline = Date.now() + 10 * 60_000;
    do {
      await new Promise((r) => setTimeout(r, 2500));
      state = await photosApi(`/sessions/${encodeURIComponent(session.id)}`, token);
      if (Date.now() > deadline) throw new Error("Google Photos selection timed out");
      if (tab.closed && !state.mediaItemsSet) return []; // user closed without picking
    } while (!state.mediaItemsSet);
    try { tab.close(); } catch { /* cross-origin — fine */ }

    const items = [];
    let pageToken = "";
    do {
      const q = new URLSearchParams({ sessionId: session.id, pageSize: "100" });
      if (pageToken) q.set("pageToken", pageToken);
      const page = await photosApi(`/mediaItems?${q}`, token);
      items.push(...(page.mediaItems || []));
      pageToken = page.nextPageToken || "";
    } while (pageToken);

    onDownloadStart?.(items.length);
    return await Promise.all(
      items.map(async (item, index) => {
        const media = item.mediaFile;
        const isVideo = (media.mimeType || "").startsWith("video/");
        // "=d" downloads image bytes, "=dv" downloads video bytes.
        const url = `${media.baseUrl}${isVideo ? "=dv" : "=d"}`;
        const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        return blobAsFile(response, media.filename || `google-photos-${index}.jpg`, media.mimeType);
      })
    );
  } finally {
    photosApi(`/sessions/${encodeURIComponent(session.id)}`, token, { method: "DELETE" }).catch(() => {});
  }
}

// ---------- Dropbox Chooser ----------
export async function pickDropboxFiles({ onDownloadStart } = {}) {
  await loadScript("https://www.dropbox.com/static/api/2/dropins.js", "dropboxjs", {
    "data-app-key": DROPBOX_APP_KEY,
  });
  return new Promise((resolve, reject) => {
    if (!window.Dropbox) return reject(new Error("Dropbox Chooser failed to load"));
    window.Dropbox.choose({
      linkType: "direct",
      multiselect: true,
      extensions: ["images", "video"],
      success: async (entries) => {
        try {
          onDownloadStart?.(entries.length);
          const files = await Promise.all(
            entries.map(async (entry) => {
              // No custom headers — they'd trigger a failing CORS preflight.
              const response = await fetch(entry.link);
              return blobAsFile(response, entry.name);
            })
          );
          resolve(files);
        } catch (e) {
          reject(e);
        }
      },
      cancel: () => resolve([]),
    });
  });
}
